import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../AppIcon';
import Button from './Button';
import { notificationService } from '../../services/notificationService';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useLanguage } from '../../contexts/LanguageContext';

const NotificationBell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const channelRef = useRef(null);
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const unreadCount = notifications?.filter(n => !n?.is_read)?.length;

  const loadNotifications = useCallback(async () => {
    if (!user?.id) return;
    setLoading(true);
    const { data } = await notificationService?.getUserNotifications(user?.id);
    setNotifications(data || []);
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    loadNotifications();

    // Subscribe to real-time new notifications
    channelRef.current = notificationService?.subscribeToUserNotifications(
      user?.id,
      (newNotification) => {
        setNotifications(prev => [newNotification, ...prev]);
      }
    );

    return () => {
      if (channelRef?.current) {
        supabase?.removeChannel(channelRef?.current);
        channelRef.current = null;
      }
    };
  }, [user?.id, loadNotifications]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (event) => {
      if (event?.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'task_assigned':
        return { name: 'ClipboardList', color: 'var(--color-accent)' };
      case 'task_overdue':
        return { name: 'AlertTriangle', color: 'var(--color-warning)' };
      case 'snag_raised':
        return { name: 'AlertCircle', color: 'var(--color-error)' };
      case 'document_expiring':
        return { name: 'FileWarning', color: 'var(--color-warning)' };
      case 'warning':
        return { name: 'AlertTriangle', color: 'var(--color-warning)' };
      case 'error':
        return { name: 'AlertCircle', color: 'var(--color-error)' };
      case 'success':
        return { name: 'CheckCircle2', color: 'var(--color-success)' };
      default:
        return { name: 'Bell', color: 'var(--color-foreground)' };
    }
  };

  const formatTimestamp = (createdAt) => {
    if (!createdAt) return '';
    const now = new Date();
    const date = new Date(createdAt);
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString();
  };

  const handleNotificationClick = async (notification) => {
    if (!notification?.is_read) {
      await notificationService?.markAsRead(notification?.id);
      setNotifications(prev =>
        prev?.map(n => n?.id === notification?.id ? { ...n, is_read: true } : n)
      );
    }
    if (notification?.link_url) {
      setIsOpen(false);
      navigate(notification?.link_url);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return;
    await notificationService?.markAllAsRead(user?.id);
    setNotifications(prev => prev?.map(n => ({ ...n, is_read: true })));
  };

  const handleClearAll = () => {
    setNotifications([]);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className="relative"
        aria-label="Notifications"
        aria-expanded={isOpen}
      >
        <Icon name="Bell" size={20} />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 flex items-center justify-center w-5 h-5 text-xs font-bold text-error-foreground bg-error rounded-full">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>
      {isOpen && (
        <div className="absolute right-0 mt-2 w-96 max-sm:fixed max-sm:inset-x-2 max-sm:top-[calc(env(safe-area-inset-top,0px)+60px)] max-sm:w-auto max-sm:mt-0 bg-popover border border-border rounded-lg shadow-elevation-4 z-dropdown">
          <div className="flex items-center justify-between p-4 border-b border-border">
            <h3 className="font-semibold text-foreground font-heading">{t('notifications')}</h3>
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleMarkAllAsRead}
                className="text-accent hover:text-accent"
              >
                {t('markAllRead')}
              </Button>
            )}
          </div>

          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-accent border-t-transparent rounded-full animate-spin" />
              </div>
            ) : notifications?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 px-4">
                <Icon name="Bell" size={48} className="text-muted-foreground mb-3" />
                <p className="text-muted-foreground text-center">{t('noNotifications')}</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {notifications?.map((notification) => {
                  const iconConfig = getNotificationIcon(notification?.type);
                  return (
                    <button
                      key={notification?.id}
                      onClick={() => handleNotificationClick(notification)}
                      className={`w-full text-left p-4 transition-smooth hover:bg-muted ${
                        !notification?.is_read ? 'bg-accent/5' : ''
                      }`}
                    >
                      <div className="flex gap-3">
                        <div className="flex-shrink-0 mt-1">
                          <Icon name={iconConfig?.name} size={20} color={iconConfig?.color} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2 mb-1">
                            <p className="font-medium text-foreground">{notification?.title}</p>
                            {!notification?.is_read && (
                              <span className="flex-shrink-0 w-2 h-2 bg-accent rounded-full mt-1.5"></span>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground mb-2">{notification?.message}</p>
                          <p className="text-xs text-muted-foreground font-caption">
                            {formatTimestamp(notification?.created_at)}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {notifications?.length > 0 && (
            <div className="p-3 border-t border-border">
              <Button
                variant="ghost"
                onClick={handleClearAll}
                className="w-full text-destructive hover:text-destructive hover:bg-destructive/10"
              >
                {t('clearAllNotifications')}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;